import { useQuery } from '@tanstack/react-query'
import {
  MonitorIcon,
  LayoutDashboardIcon,
  HardDriveIcon,
  CpuIcon,
  DollarSignIcon,
  AlertCircleIcon,
  TrendingUpIcon,
  PlayIcon,
  SquareIcon,
  PlusIcon,
  BellIcon,
  ZapIcon,
  ShieldIcon,
  ArrowRightIcon,
  SettingsIcon,
  DatabaseIcon,
  TerminalIcon,
  FileTextIcon,
  LogInIcon,
  RotateCcwIcon,
  ActivityIcon,
  InfoIcon,
  AlertTriangleIcon,
} from 'lucide-react'
import { useState } from 'react'
import { Link } from 'react-router-dom'
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts'

import { ResponsiveActionRow, ResponsivePageHeader } from '@nebula/shared/components'
import {
  endCustomerDashboardApi,
  formatDashboardCurrency,
  formatDashboardStorage,
  formatRelativeTime,
} from '@nebula/shared/services'
import type {
  CustomerDashboardResponse,
  DashboardAlert,
  RecentActivity,
  DashboardVM,
} from '@nebula/shared/services'
import { useAuthStore } from '@nebula/shared/store'

import InfraSetupBanner from '../components/InfraSetupBanner'
import { useInfraStatus } from '../hooks/useInfraStatus'

const SkeletonBar = ({ className }: { className: string }) => (
  <div className={`animate-pulse rounded bg-gray-200 ${className}`} aria-hidden="true" />
)

const UnavailableBlock = ({ className }: { className: string }) => (
  <div className={`flex flex-col items-center justify-center text-gray-500 ${className}`}>
    <AlertTriangleIcon className="mb-3 size-10 text-gray-300" />
    <p className="font-medium">Dados indisponíveis no momento</p>
  </div>
)

const DashboardPage = () => {
  const [autoRefresh, setAutoRefresh] = useState(true)
  const infraStatus = useInfraStatus()

  // Org ativa: mesma fonte do header X-Organization-Id (auth.ts). Escopar a query por org
  // evita que ela dispare antes de a org estar resolvida — o que mandava a request sem o
  // header e fazia o backend cair na org default (não-EC), respondendo 200 com stats
  // zerado (0/0 VMs, R$ 0,00, "Nenhuma VM encontrada"). Também evita cache cruzado ao
  // trocar de org. Mesmo tratamento da AnalyticsPage. Ver issue #726.
  const activeOrgId = useAuthStore((s) => s.activeOrganizationId)

  // Fetch dashboard data from API
  const {
    data: dashboardData,
    isLoading,
    error,
    refetch,
    dataUpdatedAt,
  } = useQuery<CustomerDashboardResponse>({
    queryKey: ['customer-dashboard', activeOrgId],
    queryFn: () => endCustomerDashboardApi.getCustomerDashboard(),
    enabled: !!activeOrgId,
    refetchInterval: autoRefresh ? 30_000 : false,
    staleTime: 10_000,
    retry: 2,
  })

  // Helper: Get status badge
  const getStatusBadge = (status: string) => {
    const statusConfig: Record<string, { color: string; text: string }> = {
      running: { color: 'bg-green-100 text-green-800', text: 'Online' },
      stopped: { color: 'bg-red-100 text-red-800', text: 'Parada' },
      creating: { color: 'bg-yellow-100 text-yellow-800', text: 'Criando' },
      starting: { color: 'bg-yellow-100 text-yellow-800', text: 'Iniciando' },
      stopping: { color: 'bg-orange-100 text-orange-800', text: 'Parando' },
      error: { color: 'bg-red-100 text-red-800', text: 'Erro' },
    }
    const config = statusConfig[status] || { color: 'bg-gray-100 text-gray-800', text: status }
    return (
      <span
        className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${config.color}`}
      >
        {config.text}
      </span>
    )
  }

  // Helper: Get alert icon by severity
  const getAlertIcon = (severity: string) => {
    switch (severity) {
      case 'high':
        return <AlertCircleIcon className="size-5 text-red-500" />
      case 'medium':
        return <AlertCircleIcon className="size-5 text-yellow-500" />
      case 'low':
        return <AlertCircleIcon className="size-5 text-blue-500" />
      default:
        return <AlertCircleIcon className="size-5 text-gray-500" />
    }
  }

  // Helper: Get activity icon component
  const getActivityIcon = (type: string) => {
    const icons: Record<string, React.ElementType> = {
      vm_created: PlusIcon,
      vm_started: PlayIcon,
      vm_stopped: SquareIcon,
      vm_restarted: RotateCcwIcon,
      vm_action: ZapIcon,
      login: LogInIcon,
      backup_completed: ShieldIcon,
      alert_triggered: AlertCircleIcon,
      payment_processed: DollarSignIcon,
      info: InfoIcon,
    }
    return icons[type] || ActivityIcon
  }

  // Helper: Get activity color
  const getActivityColor = (type: string) => {
    const colors: Record<string, string> = {
      vm_created: 'text-green-600',
      vm_started: 'text-green-600',
      vm_stopped: 'text-red-600',
      vm_restarted: 'text-purple-600',
      vm_action: 'text-blue-600',
      login: 'text-gray-600',
      backup_completed: 'text-blue-600',
      alert_triggered: 'text-yellow-600',
      payment_processed: 'text-emerald-600',
      info: 'text-gray-600',
    }
    return colors[type] || 'text-gray-600'
  }

  // Format last update time
  const lastUpdateTime = dataUpdatedAt
    ? new Date(dataUpdatedAt).toLocaleTimeString('pt-BR')
    : '--:--:--'

  // Primeiro carregamento. Precisa do `!activeOrgId` explícito: com `enabled: false` a
  // query não fica em isLoading (v5: isLoading = isPending && isFetching), então sem ele a
  // tela mostraria "sem dado" antes mesmo do primeiro fetch.
  const isBootstrapping = !activeOrgId || isLoading

  // Erro só derruba a tela quando não há dado nenhum: num refetch que falha sobre cache
  // válido (rede instável, 502 momentâneo), os números ficam e a falha vira aviso discreto.
  if (error && !dashboardData) {
    return (
      <div className="flex min-h-[400px] items-center justify-center p-6">
        <div className="text-center">
          <AlertTriangleIcon className="mx-auto mb-4 size-12 text-red-500" />
          <p className="mb-2 font-medium text-gray-900">Erro ao carregar dashboard</p>
          <p className="mb-4 text-gray-600">Não foi possível carregar os dados. Tente novamente.</p>
          <button
            onClick={() => refetch()}
            className="rounded-lg bg-purple-600 px-4 py-2 text-white hover:bg-purple-700"
          >
            Tentar novamente
          </button>
        </div>
      </div>
    )
  }

  // Sem fallback numérico: `stats` fica indefinido até a API responder (issue #726 — um 0
  // renderizado como dado é indistinguível de "cliente sem recursos").
  const stats = dashboardData?.stats
  const hasData = !!dashboardData
  const billingHistory = dashboardData?.billing_history || []
  const alerts = dashboardData?.alerts || []
  const recentActivities = dashboardData?.recent_activities || []
  const vms = dashboardData?.vms || []

  // O InfraSetupBanner já trata isso (não aparece enquanto isLoading); a quick action
  // afirmava "Configurar rede primeiro" antes de o useInfraStatus responder.
  const infraBlocked = !infraStatus.isReady && !infraStatus.isLoading

  // Contador só exibe número vindo da API: carregando -> skeleton; sem dado -> "—".
  const renderStat = (value: React.ReactNode, skeletonWidth = 'w-24') => {
    if (isBootstrapping) return <SkeletonBar className={`h-8 ${skeletonWidth} my-1`} />
    return <div className="text-2xl font-bold text-gray-900">{hasData ? value : '—'}</div>
  }

  return (
    <div className="mobile-page-padding">
      <ResponsivePageHeader
        title="Dashboard"
        description="Visão geral dos seus recursos e infraestrutura"
        icon={LayoutDashboardIcon}
        actions={
          <ResponsiveActionRow justify="end" className="w-full sm:w-auto">
            <div className="flex flex-col items-end gap-1">
              <button
                onClick={() => setAutoRefresh(!autoRefresh)}
                className={`inline-flex items-center rounded-lg px-3 py-1.5 text-xs font-medium ${
                  autoRefresh ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'
                }`}
              >
                Auto-refresh {autoRefresh ? 'ligado' : 'desligado'}
              </button>
              <div className="text-[10px] text-gray-500">Última atualização: {lastUpdateTime}</div>
            </div>
          </ResponsiveActionRow>
        }
      />

      {error && hasData && (
        <div className="mb-6 flex flex-wrap items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-4 py-2 text-sm text-amber-800">
          <AlertTriangleIcon className="size-4 shrink-0" />
          <span>
            Não foi possível atualizar os dados. Exibindo a última leitura ({lastUpdateTime}).
          </span>
          <button onClick={() => refetch()} className="font-medium underline">
            Tentar novamente
          </button>
        </div>
      )}

      <InfraSetupBanner infraStatus={infraStatus} />

      {/* Quick Actions */}
      <div className="mb-8">
        <h2 className="mb-4 text-lg font-semibold text-gray-900">Ações Rápidas</h2>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
          <Link
            to="/marketplace"
            className={`flex items-center space-x-3 rounded-lg bg-white p-4 shadow transition-shadow hover:shadow-md ${infraBlocked ? 'opacity-60' : ''}`}
          >
            <div className={`rounded-lg p-2 ${infraBlocked ? 'bg-amber-100' : 'bg-purple-100'}`}>
              <PlusIcon
                className={`size-5 ${infraBlocked ? 'text-amber-600' : 'text-purple-600'}`}
              />
            </div>
            <div>
              <div className="font-medium text-gray-900">Nova VM</div>
              {infraStatus.isLoading ? (
                <SkeletonBar className="mt-1 h-4 w-32" />
              ) : (
                <div
                  className={`text-sm ${infraStatus.isReady ? 'text-gray-500' : 'text-amber-600'}`}
                >
                  {infraStatus.isReady ? 'Criar instância' : 'Configurar rede primeiro'}
                </div>
              )}
            </div>
          </Link>
          <Link
            to="/backups"
            className="flex items-center space-x-3 rounded-lg bg-white p-4 shadow transition-shadow hover:shadow-md"
          >
            <div className="rounded-lg bg-blue-100 p-2">
              <ShieldIcon className="size-5 text-blue-600" />
            </div>
            <div>
              <div className="font-medium text-gray-900">Backup</div>
              <div className="text-sm text-gray-500">Criar backup</div>
            </div>
          </Link>
          <Link
            to="/billing"
            className="flex items-center space-x-3 rounded-lg bg-white p-4 shadow transition-shadow hover:shadow-md"
          >
            <div className="rounded-lg bg-emerald-100 p-2">
              <FileTextIcon className="size-5 text-emerald-600" />
            </div>
            <div>
              <div className="font-medium text-gray-900">Fatura</div>
              <div className="text-sm text-gray-500">Ver cobrança</div>
            </div>
          </Link>
          <Link
            to="/support"
            className="flex items-center space-x-3 rounded-lg bg-white p-4 shadow transition-shadow hover:shadow-md"
          >
            <div className="rounded-lg bg-orange-100 p-2">
              <BellIcon className="size-5 text-orange-600" />
            </div>
            <div>
              <div className="font-medium text-gray-900">Suporte</div>
              <div className="text-sm text-gray-500">Abrir ticket</div>
            </div>
          </Link>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="mb-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-5">
        <div className="rounded-lg bg-white p-6 shadow">
          <div className="mb-4 flex items-center justify-between">
            <MonitorIcon className="size-8 text-purple-600" />
            <TrendingUpIcon className="size-4 text-green-500" />
          </div>
          {renderStat(stats && `${stats.active_vms}/${stats.total_vms}`)}
          <div className="text-sm text-gray-600">VMs Ativas</div>
        </div>

        <div className="rounded-lg bg-white p-6 shadow">
          <div className="mb-4 flex items-center justify-between">
            <CpuIcon className="size-8 text-blue-600" />
            <span className="text-xs text-gray-500">cores</span>
          </div>
          {renderStat(stats?.cpu_allocated, 'w-16')}
          <div className="text-sm text-gray-600">CPU Alocada</div>
        </div>

        <div className="rounded-lg bg-white p-6 shadow">
          <div className="mb-4 flex items-center justify-between">
            <DatabaseIcon className="size-8 text-green-600" />
            <span className="text-xs text-gray-500">GB</span>
          </div>
          {renderStat(stats?.ram_allocated, 'w-16')}
          <div className="text-sm text-gray-600">RAM Alocada</div>
        </div>

        <div className="rounded-lg bg-white p-6 shadow">
          <div className="mb-4 flex items-center justify-between">
            <HardDriveIcon className="size-8 text-indigo-600" />
            <TrendingUpIcon className="size-4 text-green-500" />
          </div>
          {renderStat(stats && formatDashboardStorage(stats.storage_used))}
          <div className="text-sm text-gray-600">Storage Alocado</div>
        </div>

        <div className="rounded-lg bg-white p-6 shadow">
          <div className="mb-4 flex items-center justify-between">
            <DollarSignIcon className="size-8 text-emerald-600" />
            <TrendingUpIcon className="size-4 text-green-500" />
          </div>
          {renderStat(stats && formatDashboardCurrency(stats.monthly_cost), 'w-28')}
          <div className="text-sm text-gray-600">Fatura Atual</div>
        </div>
      </div>

      {/* Charts Row */}
      <div className="mb-8 grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* Resource Summary Card */}
        <div className="rounded-lg bg-white shadow">
          <div className="border-b p-6">
            <h2 className="text-lg font-semibold text-gray-900">Resumo de Recursos</h2>
          </div>
          <div className="p-6">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 sm:gap-6">
              <div className="rounded-lg bg-blue-50 p-4 text-center">
                <CpuIcon className="mx-auto mb-2 size-8 text-blue-600" />
                <div className="flex justify-center">
                  {renderStat(stats?.cpu_allocated, 'w-16')}
                </div>
                <div className="text-sm text-gray-600">vCPUs Totais</div>
              </div>
              <div className="rounded-lg bg-purple-50 p-4 text-center">
                <DatabaseIcon className="mx-auto mb-2 size-8 text-purple-600" />
                <div className="flex justify-center">
                  {renderStat(stats && `${stats.ram_allocated} GB`)}
                </div>
                <div className="text-sm text-gray-600">RAM Total</div>
              </div>
              <div className="rounded-lg bg-indigo-50 p-4 text-center">
                <HardDriveIcon className="mx-auto mb-2 size-8 text-indigo-600" />
                <div className="flex justify-center">
                  {renderStat(stats && formatDashboardStorage(stats.storage_used))}
                </div>
                <div className="text-sm text-gray-600">Storage Total</div>
              </div>
              <div className="rounded-lg bg-green-50 p-4 text-center">
                <MonitorIcon className="mx-auto mb-2 size-8 text-green-600" />
                <div className="flex justify-center">{renderStat(stats?.total_vms, 'w-16')}</div>
                <div className="text-sm text-gray-600">VMs Totais</div>
              </div>
            </div>
          </div>
        </div>

        {/* Billing Chart */}
        <div className="rounded-lg bg-white shadow">
          <div className="border-b p-6">
            <h2 className="text-lg font-semibold text-gray-900">Histórico de Cobrança</h2>
          </div>
          <div className="p-6">
            {isBootstrapping ? (
              <SkeletonBar className="h-[300px] w-full" />
            ) : hasData ? (
              billingHistory.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={billingHistory}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month_label" />
                    <YAxis />
                    <Tooltip formatter={(value) => formatDashboardCurrency(Number(value))} />
                    <Line type="monotone" dataKey="amount" stroke="#10b981" strokeWidth={3} />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex h-[300px] flex-col items-center justify-center text-gray-500">
                  <FileTextIcon className="mb-4 size-12 text-gray-300" />
                  <p className="text-lg font-medium">Nenhuma cobrança registrada</p>
                  <p className="text-sm">O histórico aparecerá aqui quando houver transações</p>
                </div>
              )
            ) : (
              <UnavailableBlock className="h-[300px]" />
            )}
          </div>
        </div>
      </div>

      {/* Alerts and Activity Row */}
      <div className="mb-8 grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* Active Alerts */}
        <div className="rounded-lg bg-white shadow">
          <div className="border-b p-6">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900">Alertas Ativos</h2>
              {alerts.length > 0 && (
                <span className="rounded-full bg-red-100 px-2 py-1 text-xs font-medium text-red-800">
                  {alerts.length}
                </span>
              )}
            </div>
          </div>
          <div className="p-6">
            {isBootstrapping ? (
              <SkeletonBar className="h-24 w-full" />
            ) : hasData ? (
              alerts.length > 0 ? (
                <div className="space-y-4">
                  {alerts.map((alert: DashboardAlert) => (
                    <div
                      key={alert.id}
                      className="flex items-start space-x-3 rounded-lg bg-gray-50 p-3"
                    >
                      {getAlertIcon(alert.severity)}
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-medium text-gray-900">{alert.title}</div>
                        <div className="text-sm text-gray-500">{alert.description}</div>
                        <div className="mt-1 text-xs text-gray-400">
                          {formatRelativeTime(alert.timestamp)}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center py-8 text-gray-500">
                  <ShieldIcon className="mb-3 size-10 text-green-400" />
                  <p className="font-medium text-green-600">Tudo certo!</p>
                  <p className="text-sm">Nenhum alerta ativo no momento</p>
                </div>
              )
            ) : (
              <UnavailableBlock className="py-8" />
            )}
          </div>
        </div>

        {/* Recent Activity */}
        <div className="rounded-lg bg-white shadow">
          <div className="border-b p-6">
            <h2 className="text-lg font-semibold text-gray-900">Atividade Recente</h2>
          </div>
          <div className="p-6">
            {isBootstrapping ? (
              <SkeletonBar className="h-24 w-full" />
            ) : hasData ? (
              recentActivities.length > 0 ? (
                <div className="space-y-4">
                  {recentActivities.map((activity: RecentActivity) => {
                    const IconComponent = getActivityIcon(activity.type)
                    const iconColor = getActivityColor(activity.type)
                    return (
                      <div key={activity.id} className="flex items-start space-x-3">
                        <div className="rounded-lg bg-gray-100 p-2">
                          <IconComponent className={`size-4 ${iconColor}`} />
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="text-sm font-medium text-gray-900">{activity.title}</div>
                          <div className="text-sm text-gray-500">{activity.description}</div>
                          <div className="mt-1 text-xs text-gray-400">
                            {formatRelativeTime(activity.timestamp)}
                          </div>
                        </div>
                      </div>
                    )
                  })}
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center py-8 text-gray-500">
                  <ActivityIcon className="mb-3 size-10 text-gray-300" />
                  <p className="font-medium">Sem atividade recente</p>
                  <p className="text-sm">As atividades aparecerão aqui</p>
                </div>
              )
            ) : (
              <UnavailableBlock className="py-8" />
            )}
          </div>
        </div>
      </div>

      {/* VMs Overview */}
      <div className="rounded-lg bg-white shadow">
        <div className="border-b p-6">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <h2 className="text-lg font-semibold text-gray-900">Suas Máquinas Virtuais</h2>
            <Link
              to="/instances"
              className="flex items-center space-x-1 text-sm text-purple-600 hover:text-purple-800"
            >
              <span>Ver todas</span>
              <ArrowRightIcon className="size-3" />
            </Link>
          </div>
        </div>
        {isBootstrapping ? (
          <div className="space-y-3 p-6">
            <SkeletonBar className="h-12 w-full" />
            <SkeletonBar className="h-12 w-full" />
            <SkeletonBar className="h-12 w-full" />
          </div>
        ) : hasData ? (
          vms.length > 0 ? (
            <div>
              <div className="divide-y divide-gray-200 md:hidden">
                {vms.map((vm: DashboardVM) => (
                  <div key={vm.id} className="space-y-3 p-4">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="truncate text-sm font-medium text-gray-900">{vm.name}</div>
                        <div className="text-xs text-gray-500">{vm.os}</div>
                      </div>
                      {getStatusBadge(vm.status)}
                    </div>
                    <div className="text-xs text-gray-600">
                      {vm.cpu.allocated} vCPU - {vm.memory.allocated} GB RAM -{' '}
                      {vm.storage.allocated} GB
                    </div>
                    <div className="text-xs text-gray-500">
                      IP: <span className="font-mono text-gray-700">{vm.ip || '-'}</span>
                    </div>
                    <div className="text-xs text-gray-500">Local: {vm.location}</div>
                    <div className="flex flex-wrap items-center gap-2">
                      <Link
                        to={`/instances/${vm.id}/console`}
                        className="inline-flex items-center gap-1 rounded bg-purple-100 px-2.5 py-1 text-xs font-medium text-purple-700"
                      >
                        <TerminalIcon className="size-3.5" />
                        Console
                      </Link>
                      <Link
                        to={`/instances/${vm.id}`}
                        className="inline-flex items-center gap-1 rounded bg-gray-100 px-2.5 py-1 text-xs font-medium text-gray-700"
                      >
                        <SettingsIcon className="size-3.5" />
                        Detalhes
                      </Link>
                      {vm.status === 'running' ? (
                        <button className="inline-flex items-center gap-1 rounded bg-red-100 px-2.5 py-1 text-xs font-medium text-red-700">
                          <SquareIcon className="size-3.5" />
                          Parar
                        </button>
                      ) : (
                        <button className="inline-flex items-center gap-1 rounded bg-green-100 px-2.5 py-1 text-xs font-medium text-green-700">
                          <PlayIcon className="size-3.5" />
                          Iniciar
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              <div className="hidden overflow-x-auto md:block">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium tracking-wider text-gray-500 uppercase">
                        Nome
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium tracking-wider text-gray-500 uppercase">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium tracking-wider text-gray-500 uppercase">
                        Recursos
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium tracking-wider text-gray-500 uppercase">
                        IP
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium tracking-wider text-gray-500 uppercase">
                        Local
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium tracking-wider text-gray-500 uppercase">
                        Ações
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {vms.map((vm: DashboardVM) => (
                      <tr key={vm.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div>
                            <div className="text-sm font-medium text-gray-900">{vm.name}</div>
                            <div className="text-sm text-gray-500">{vm.os}</div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">{getStatusBadge(vm.status)}</td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">
                            {vm.cpu.allocated} vCPU - {vm.memory.allocated} GB RAM
                          </div>
                          <div className="text-sm text-gray-500">
                            {vm.storage.allocated} GB Storage
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="font-mono text-sm text-gray-900">{vm.ip || '-'}</div>
                        </td>
                        <td className="px-6 py-4 text-sm whitespace-nowrap text-gray-500">
                          {vm.location}
                        </td>
                        <td className="px-6 py-4 text-sm font-medium whitespace-nowrap">
                          <div className="flex items-center space-x-2">
                            <Link
                              to={`/instances/${vm.id}/console`}
                              className="text-purple-600 hover:text-purple-900"
                              title="Console"
                            >
                              <TerminalIcon className="size-4" />
                            </Link>
                            {vm.status === 'running' ? (
                              <button className="text-red-600 hover:text-red-900" title="Parar">
                                <SquareIcon className="size-4" />
                              </button>
                            ) : (
                              <button
                                className="text-green-600 hover:text-green-900"
                                title="Iniciar"
                              >
                                <PlayIcon className="size-4" />
                              </button>
                            )}
                            <Link
                              to={`/instances/${vm.id}`}
                              className="text-gray-400 hover:text-gray-600"
                              title="Configurações"
                            >
                              <SettingsIcon className="size-4" />
                            </Link>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            <div className="p-12 text-center">
              <MonitorIcon className="mx-auto mb-4 size-12 text-gray-300" />
              <p className="mb-2 text-lg font-medium text-gray-900">Nenhuma VM encontrada</p>
              <p className="mb-4 text-gray-500">Você ainda não tem máquinas virtuais criadas</p>
              <Link
                to="/marketplace"
                className="inline-flex items-center rounded-lg bg-purple-600 px-4 py-2 text-white hover:bg-purple-700"
              >
                <PlusIcon className="mr-2 size-4" />
                Criar primeira VM
              </Link>
            </div>
          )
        ) : (
          <UnavailableBlock className="p-12" />
        )}
      </div>
    </div>
  )
}

export default DashboardPage
